import React from "react";
/* eslint-disable react/no-unescaped-entities */
import {
    BookOpen, Play, Square, RefreshCw, Settings as SettingsIcon, ShieldAlert,
    Globe2, Power, Zap, Activity, AlertTriangle, FileDown, Wifi, WifiOff,
    Layers, Lock, Server, Database, ArrowLeft,
} from "lucide-react";

const SectionTitle = ({ icon: Icon, children, anchor }) => (
    <div id={anchor} className="flex items-center gap-2 mt-10 mb-4 scroll-mt-24">
        {Icon && (
            <div
                className="w-7 h-7 rounded-md flex items-center justify-center"
                style={{ background: "rgba(46,230,214,0.10)", border: "1px solid rgba(46,230,214,0.25)" }}
            >
                <Icon size={14} color="#2EE6D6" />
            </div>
        )}
        <h2 className="text-base font-semibold text-[#E6EDF6] tracking-[0.04em]">{children}</h2>
    </div>
);

const SubTitle = ({ children, anchor }) => (
    <h3
        id={anchor}
        className="text-[11px] uppercase tracking-[0.18em] text-[#A7B4C6] mt-6 mb-2 scroll-mt-24"
    >
        {children}
    </h3>
);

const Card = ({ children, accent = "rgba(167,180,198,0.18)", bg = "rgba(15,22,32,0.55)" }) => (
    <div
        className="rounded-lg p-4 mb-3"
        style={{ background: bg, border: `1px solid ${accent}` }}
    >
        {children}
    </div>
);

const ButtonRow = ({ icon: Icon, name, action, who, color = "#2EE6D6", testid }) => (
    <Card accent={`${color}22`} bg="rgba(15,22,32,0.45)">
        <div className="flex items-start gap-3">
            <div
                className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center mt-0.5"
                style={{ background: `${color}1A`, border: `1px solid ${color}40` }}
            >
                {Icon && <Icon size={15} color={color} />}
            </div>
            <div className="flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-semibold text-[#E6EDF6]" data-testid={testid}>{name}</span>
                    {who && (
                        <span
                            className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.14em] font-mono"
                            style={{ background: `${color}1A`, color, border: `1px solid ${color}40` }}
                        >
                            {who}
                        </span>
                    )}
                </div>
                <div className="text-[12px] text-[#A7B4C6] mt-1 leading-relaxed">{action}</div>
            </div>
        </div>
    </Card>
);

const Pair = ({ k, v, color = "#A7B4C6" }) => (
    <div className="flex items-baseline gap-2">
        <span className="text-[10px] uppercase tracking-[0.14em] text-[#6F7D92] min-w-[110px]">{k}</span>
        <span className="text-[12px] font-mono" style={{ color }}>{v}</span>
    </div>
);

const Codey = ({ children }) => (
    <code
        className="px-1.5 py-0.5 rounded text-[11px] font-mono"
        style={{ background: "rgba(167,180,198,0.10)", color: "#E6EDF6", border: "1px solid rgba(167,180,198,0.15)" }}
    >
        {children}
    </code>
);

export default function DocsPage({ onBack }) {
    return (
        <div className="min-h-screen text-[#E6EDF6]" data-testid="docs-page" style={{ background: "#0B0F14" }}>
            {/* Top bar */}
            <div
                className="sticky top-0 z-40 backdrop-blur-md"
                style={{ background: "rgba(11,15,20,0.85)", borderBottom: "1px solid #1B2A3A" }}
            >
                <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
                    <div className="flex items-center gap-2.5">
                        <div
                            className="w-7 h-7 rounded-lg flex items-center justify-center"
                            style={{ background: "linear-gradient(135deg, rgba(46,230,214,0.25), rgba(61,167,255,0.15))", border: "1px solid rgba(46,230,214,0.3)" }}
                        >
                            <BookOpen size={14} color="#2EE6D6" />
                        </div>
                        <div className="leading-tight">
                            <div className="text-sm font-semibold text-[#E6EDF6]">Documentation</div>
                            <div className="text-[10px] uppercase tracking-[0.16em] text-[#6F7D92]">
                                AsterDex GridDesk · v1.0
                            </div>
                        </div>
                    </div>
                    <div className="ml-auto">
                        <button
                            type="button"
                            onClick={onBack}
                            data-testid="docs-back-to-dashboard"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium"
                            style={{ background: "rgba(167,180,198,0.08)", border: "1px solid #1B2A3A", color: "#A7B4C6" }}
                        >
                            <ArrowLeft size={13} /> Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-8">
                {/* Intro */}
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-[#E6EDF6]">
                        How the Trailing Self-Heal Grid Bot Works
                    </h1>
                    <p className="text-[13px] text-[#A7B4C6] mt-2 leading-relaxed max-w-[820px]">
                        GridDesk runs a <span className="text-[#2EE6D6]">dynamic trailing grid</span> against
                        AsterDex Spot V3 BTCUSDT. It always keeps exactly{" "}
                        <Codey>max_buys</Codey> active BUY limit orders below market. When a BUY fills, it
                        immediately places a matching SELL one <Codey>grid_size</Codey> higher. When that
                        SELL fills, it self-heals by re-creating the original BUY at the same price — a
                        closed PnL cycle. The bot persists state to MongoDB and reconciles with AsterDex on
                        every start, so a crash or restart cannot lose track of open orders.
                    </p>
                </div>

                {/* TOC */}
                <div
                    className="rounded-lg p-4 mb-6"
                    style={{ background: "rgba(15,22,32,0.6)", border: "1px solid #1B2A3A" }}
                >
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#6F7D92] mb-2">Contents</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[12px]">
                        {[
                            ["controls", "Header Controls"],
                            ["panels", "Dashboard Panels"],
                            ["sgb-strategy", "SmartGrid Strategy (Dalton)"],
                            ["badges", "Status Indicators"],
                            ["safety", "Safety Features"],
                            ["edge", "Edge Cases & Recovery"],
                            ["api", "Backend API Reference"],
                        ].map(([id, label]) => (
                            <a
                                key={id}
                                href={`#${id}`}
                                className="text-[#A7B4C6] hover:text-[#2EE6D6] transition-colors"
                            >
                                · {label}
                            </a>
                        ))}
                    </div>
                </div>

                {/* === 1. Header controls === */}
                <SectionTitle icon={Activity} anchor="controls">Header Controls</SectionTitle>
                <p className="text-[12px] text-[#A7B4C6] mb-4">
                    Every button in the top bar, what it does, and which side effects it has.
                </p>

                <ButtonRow
                    icon={Play}
                    name="Start"
                    who="Operator"
                    color="#2EE6D6"
                    testid="docs-btn-start"
                    action={(
                        <>
                            Launches the bot loop. The bot subscribes to the AsterDex market WebSocket,
                            seeds the engine with the latest BTCUSDT price, and places up to{" "}
                            <Codey>max_buys</Codey> BUY limit orders starting at{" "}
                            <Codey>price - grid_size</Codey>. <br />
                            <span className="text-[#FFB020]">In PAPER:</span> orders only exist in memory + DB. <br />
                            <span className="text-[#FF5C6C]">In LIVE:</span> orders are signed via EIP-712 and
                            submitted to AsterDex Spot V3.
                        </>
                    )}
                />
                <ButtonRow
                    icon={Square}
                    name="Stop"
                    who="Operator"
                    color="#FF5C6C"
                    testid="docs-btn-stop"
                    action={(
                        <>
                            Asks for confirmation, then stops the engine cleanly: cancels the price WS,
                            disconnects the user-data WS, and pauses placement. <em>Existing open orders
                            on AsterDex are NOT cancelled automatically</em> — they keep working so a
                            crash does not leave you with no exits. Use kill-switch + a manual cancel if
                            you want to fully wind down.
                        </>
                    )}
                />
                <ButtonRow
                    icon={RefreshCw}
                    name="Recover"
                    who="Operator"
                    color="#3DA7FF"
                    testid="docs-btn-recover"
                    action={(
                        <>
                            <span className="text-[#FFB020]">PAPER:</span> resets the paper ladder to a
                            fresh state. <br />
                            <span className="text-[#FF5C6C]">LIVE:</span> calls{" "}
                            <Codey>GET /api/v3/openOrders</Codey> on AsterDex, pulls every{" "}
                            <Codey>gd-</Codey>‑prefixed order owned by your wallet, re-hydrates the engine
                            with them (BUYs + SELLs), and writes their status back to MongoDB. Run this
                            after a server crash, container restart, or whenever the dashboard&apos;s
                            in-memory view drifts from the exchange.
                        </>
                    )}
                />
                <ButtonRow
                    icon={SettingsIcon}
                    name="Settings"
                    who="Operator"
                    color="#A7B4C6"
                    testid="docs-btn-settings"
                    action={(
                        <>
                            Opens the configuration dialog. Editable fields:{" "}
                            <Codey>grid_size</Codey>, <Codey>buy_qty</Codey>, <Codey>sell_qty</Codey>,{" "}
                            <Codey>max_buys</Codey>, <Codey>tif</Codey> (GTX/GTC),{" "}
                            <Codey>btc_inventory_cap</Codey>, <Codey>usdt_reserve</Codey>, wallet
                            allocation, plus the Fees and Exit panels (fee mode/rates, breakeven-arm
                            cushion, structure-exit toggle) and the pump-strategy fields. Every value is
                            <b> validated server-side</b> (positive/finite, bounded rung counts, known
                            TIF) — an out-of-range value is rejected instead of persisting and silently
                            breaking the engine. Saves are written to MongoDB and apply on the next
                            engine cycle. Changing parameters does NOT mutate orders already on the
                            book.
                        </>
                    )}
                />
                <ButtonRow
                    icon={Power}
                    name="PAPER ⇄ LIVE toggle"
                    who="Operator"
                    color="#FFB020"
                    testid="docs-btn-mode-toggle"
                    action={(
                        <>
                            Switches between simulated paper trading (no real money) and live AsterDex
                            trading. Arming LIVE requires an <b>email OTP grant</b> (single-use,
                            expires in minutes) — this applies to the main bot, the SmartGrid bot AND
                            every multi-grid. When you LEAVE paper, any leftover paper orders are
                            marked <Codey>STALE</Codey> immediately so they cannot ghost into the live
                            view.
                        </>
                    )}
                />
                <ButtonRow
                    icon={Globe2}
                    name="MAINNET ⇄ TESTNET"
                    who="Operator"
                    color="#3DA7FF"
                    testid="docs-btn-network"
                    action={(
                        <>
                            Routes signed REST + WebSocket traffic to the AsterDex testnet or mainnet
                            (chainId 714 vs 1666). Requires the bot to be stopped first. Engine + paper
                            state are reset on switch (different network = different wallet account),
                            and live balances are re-fetched fresh.
                        </>
                    )}
                />

                {/* === 2. Panels === */}
                <SectionTitle icon={Layers} anchor="panels">Dashboard Panels</SectionTitle>

                <SubTitle>Grid Ladder (hero)</SubTitle>
                <Card>
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        Live view of all BUY rungs and any active SELL rungs. The thin price marker
                        in the centre is the current AsterDex mid-price. Click any rung to open the
                        Grid Drill-Down dialog showing the full order timeline, trades, partial-fill
                        progress, and realized PnL for that grid cell.
                    </p>
                    <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(167,180,198,0.15)" }}>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-[#6F7D92] mb-2">Colour legend (SmartGrid)</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-4 text-[12px]">
                            {[
                                ["#2BE38B", "Resting BUY", "classic grid rung waiting to fill"],
                                ["#FF5C6C", "Take-profit SELL", "sell placed after a BUY filled"],
                                ["#3DA7FF", "Support-zone / knife-catch", "committed Dalton entry or crash-rebound catch"],
                                ["#FFB020", "Active trailing stop", "in profit; stop ratchets up, never below buy+fees (● SL badge)"],
                                ["#C77DFF", "Double-down rung", "pyramid add into a confirmed run"],
                            ].map(([c, name, desc]) => (
                                <div key={name} className="flex items-start gap-2">
                                    <span className="w-2.5 h-2.5 mt-1 rounded-sm shrink-0" style={{ background: c }} />
                                    <span className="text-[#E6EDF6]">{name}
                                        <span className="text-[#6F7D92]"> — {desc}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>

                <SubTitle>Stats Bento</SubTitle>
                <Card>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-1">
                        <Pair k="Realized PnL" v="Sum of closed cycles (cumulative)" />
                        <Pair k="Realized PnL 24h" v="Cycles closed in the last rolling 24h" />
                        <Pair k="Cycles Completed" v="Full BUY→SELL→re-BUY iterations" />
                        <Pair k="Active BUYs/SELLs" v="Live order counts on AsterDex" />
                        <Pair k="BTC Inventory (free)" v="Wallet-owned BTC not locked in orders" color="#2BE38B" />
                        <Pair k="USDT (free)" v="Wallet-owned USDT not locked in orders" color="#2BE38B" />
                    </div>
                </Card>

                <SubTitle>Price Chart</SubTitle>
                <Card>
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        Last ~720 ticks of BTCUSDT mid-price. Overlay dashed lines show all active BUY
                        (<span className="text-[#2BE38B]">green</span>) and SELL
                        (<span className="text-[#FF5C6C]">red</span>) rungs so you can see where the
                        ladder sits relative to price. Empty until the bot is started and the first
                        tick arrives.
                    </p>
                </Card>

                <SubTitle>Operations & Security panel</SubTitle>
                <Card>
                    <ButtonRow
                        icon={ShieldAlert}
                        name="Kill-Switch (Engage / Release)"
                        who="Operator"
                        color="#FFB020"
                        testid="docs-btn-killswitch"
                        action={(
                            <>
                                Pauses placement of <em>new</em> orders without cancelling existing
                                ones. SELLs already on the book continue working so positions can wind
                                down naturally. Useful for a controlled shutdown without selling at a
                                loss. Release to resume normal placement.
                            </>
                        )}
                    />
                    <Pair k="Lifetime Counters" v="BUYs placed/cancelled, SELL fills, foreign-order ignores" />
                    <Pair k="WS Telemetry" v="Connect/disconnect count, backoff, msg count for live debugging" />
                </Card>

                <SubTitle>Exclusion Ranges</SubTitle>
                <Card>
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        Define one or more price bands that the bot must NOT place BUY orders inside.
                        Useful when you expect a strong resistance / support zone where you do not want
                        accumulation. Engine skips any rung whose price falls inside an exclusion
                        range; the rung is reattempted on the next tick after price has moved out.
                    </p>
                </Card>

                <SubTitle>Open Orders & Trade History tables</SubTitle>
                <Card>
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        The Open Orders table filters by the <em>current</em>{" "}
                        (<Codey>mode</Codey>, <Codey>use_testnet</Codey>) context only — paper orders
                        never appear when LIVE is selected, and testnet vs mainnet never mix. Trade
                        History shows realized PnL per fill plus partial-fill increments tagged
                        accordingly.
                    </p>
                </Card>

                <SubTitle>Reports (CSV exports)</SubTitle>
                <Card>
                    <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1.5 text-[12px] text-[#A7B4C6]">
                            <FileDown size={13} color="#2BE38B" /> <Codey>Trades.csv</Codey> — every fill
                            with grid_id, side, price, qty, PnL, timestamp.
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-[12px] text-[#A7B4C6]">
                            <FileDown size={13} color="#3DA7FF" /> <Codey>Daily-PnL.csv</Codey> — UTC
                            day buckets with realized PnL and cycle counts.
                        </span>
                    </div>
                </Card>

                {/* === SmartGrid Strategy (Dalton) === */}
                <SectionTitle icon={Activity} anchor="sgb-strategy">SmartGrid Strategy (Dalton) — SGB, paper/testnet</SectionTitle>
                <p className="text-[12px] text-[#A7B4C6] mb-4">
                    The autonomous SmartGrid bot can run a Market-Profile strategy from James
                    Dalton's <em>Mind Over Markets</em> / <em>Markets in Profile</em>. These overlays
                    run on paper/testnet by default — a LIVE bot trades the neutral ladder unless the
                    explicit per-strategy live opt-ins are enabled
                    (<Codey>dalton_live_entry_enabled</Codey>, <Codey>pump_chase_live_enabled</Codey>,{" "}
                    <Codey>knife_catch_live_enabled</Codey> — all default OFF, surfaced as "live gates"
                    on the Architecture page). Crash veto, inventory cap, USDT reserve and the kill
                    switch stay absolute either way.
                </p>

                <SubTitle anchor="sgb-zone">Support-zone entries</SubTitle>
                <Card>
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        A <b>separate</b> strategy that runs <b>alongside</b> the classic ladder
                        (it does not replace or suspend it): the bot rests extra committed bids at
                        strong <b style={{ color: "#3DA7FF" }}>support zones</b> — high-volume nodes
                        (prior acceptance) below price. Each armed zone is scored 0–100 every second
                        (volume strength, position vs the value area, responsive buying, excess/tails,
                        balance). While a zone's rolling-average confidence stays at or above the
                        threshold (default <Codey>65</Codey>) the bid <b>holds</b>; if it decays the
                        zone is retired and the buy <b>shifts down</b> to the next support. If no strong
                        node sits in the ideal band, a guaranteed fallback still rests one bid at the
                        nearest structural support so the strategy always works. Enabling it
                        auto-enables its dependencies; your normal grid (including order-slicing /
                        double-down) keeps running unchanged.
                    </p>
                </Card>

                <SubTitle anchor="sgb-exits">Structure exits (TP / trailing SL)</SubTitle>
                <Card>
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        A zone fill is managed as a structure position: the take-profit rests just
                        <b> below the next resistance</b> node. Once price clears buy+fees a stop is
                        armed at the <b>breakeven floor</b>; as price nears the TP, the TP rolls up
                        under the next resistance and the stop ratchets up under the nearest minor
                        support. The stop only ever moves <b>up</b> and <b>never below buy+fees</b>
                        (profit-only) — a gap-down rests the exit at breakeven rather than realizing a
                        loss. Positions with an active trailing stop show
                        <span style={{ color: "#FFB020" }}> amber</span> in the ladder with a
                        <span style={{ color: "#FFB020" }}> ● SL</span> badge.
                    </p>
                </Card>

                <SubTitle anchor="sgb-knife">Knife-catch (crash rebound)</SubTitle>
                <Card>
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        On a live crash the bot enters a <b>knife</b> state: it pauses new buys and
                        retires committed zones (never buy a falling knife). It watches for the
                        <b> rebound</b> — price bouncing off the rolling low with momentum turning up,
                        volatility rolling over and the liquidation cascade <em>intensity</em> decaying
                        (a short-window magnitude, so the bottom is confirmed near the true low instead
                        of minutes late), plus failsafes so it can never hang. At the confirmed rebound
                        the bot places <b>one</b> catch buy at the bounce — bypassing the pause for that
                        single order only, with full inventory safety (BTC cap + reserve) — then rides
                        it with a trailing stop that never sits below buy+fees.
                    </p>
                </Card>

                {/* === 3. Status badges === */}
                <SectionTitle icon={Wifi} anchor="badges">Status Indicators</SectionTitle>
                <Card>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2">
                        <Pair k="STOPPED" v="Engine paused, no WS connections, no orders placed" color="#FF5C6C" />
                        <Pair k="LIVE" v="Engine running, market WS connected" color="#2BE38B" />
                        <Pair k="PAPER badge" v="Simulated trading; balances are virtual" color="#3DA7FF" />
                        <Pair k="LIVE badge" v="Real orders signed and sent to AsterDex" color="#FFB020" />
                        <Pair k="MAINNET / TESTNET" v="Which AsterDex network the signer is targeting" color="#3DA7FF" />
                        <Pair k="WS OFFLINE" v="Market data WebSocket is reconnecting (backoff)" color="#FF5C6C" />
                        <Pair k="OWNED: gd-*" v="Bot only acts on clientOrderIds starting with gd-" color="#2EE6D6" />
                    </div>
                </Card>

                {/* === 4. Safety === */}
                <SectionTitle icon={Lock} anchor="safety">Safety Features</SectionTitle>

                <Card>
                    <ul className="list-disc pl-5 text-[12px] text-[#A7B4C6] space-y-2">
                        <li>
                            <span className="text-[#E6EDF6]">BTC inventory cap.</span> Engine refuses to
                            place a new BUY when total BTC holdings (free + locked) would exceed{" "}
                            <Codey>btc_inventory_cap</Codey>.
                        </li>
                        <li>
                            <span className="text-[#E6EDF6]">USDT reserve.</span> A minimum USDT cushion
                            is always kept aside. Engine refuses BUYs that would dip below{" "}
                            <Codey>usdt_reserve</Codey>.
                        </li>
                        <li>
                            <span className="text-[#E6EDF6]">Foreign-order isolation.</span> Any order on
                            the wallet that does not carry the <Codey>gd-</Codey> prefix is ignored — the
                            bot never cancels or fills against trades you placed manually elsewhere.
                        </li>
                        <li>
                            <span className="text-[#E6EDF6]">Context isolation.</span> Open Orders are
                            tagged with <Codey>mode</Codey> + <Codey>use_testnet</Codey>. Switching
                            paper↔live or mainnet↔testnet never bleeds orders across contexts.
                        </li>
                        <li>
                            <span className="text-[#E6EDF6]">EIP-712 signing.</span> Every LIVE request
                            is signed off-device using the wallet private key (held only on this VPS).
                            Nonces are tracked so the same payload can never be re-broadcast.
                        </li>
                    </ul>
                </Card>

                {/* === 5. Edge cases === */}
                <SectionTitle icon={AlertTriangle} anchor="edge">Edge Cases &amp; Recovery</SectionTitle>

                <SubTitle>Server crash, container restart, OS reboot</SubTitle>
                <Card accent="#FF5C6C44">
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        <span className="text-[#E6EDF6]">What you lose:</span> nothing on the exchange
                        side — open orders remain live on AsterDex. The bot&apos;s in-memory engine state
                        and ephemeral WS subscriptions reset to empty.
                    </p>
                    <p className="text-[12px] text-[#A7B4C6] mt-2 leading-relaxed">
                        <span className="text-[#E6EDF6]">What happens on restart:</span> supervisor
                        respawns the backend in &lt; 2 s. MongoDB still holds the full order/trade/event
                        ledger. The bot reads the last <Codey>bot_state</Codey> singleton and rehydrates
                        its mode + network. It then waits for you to{" "}
                        <span className="text-[#2EE6D6]">click Recover</span> (or call{" "}
                        <Codey>POST /api/bot/recover</Codey>), which pulls every <Codey>gd-</Codey>
                        order from AsterDex, replays them into the engine, and re-marks them OPEN in
                        MongoDB. No duplicate orders are placed because clientOrderIds are deterministic
                        per grid_id.
                    </p>
                </Card>

                <SubTitle>Trades that filled while the server was off</SubTitle>
                <Card accent="#2BE38B44">
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        These get reconciled in two passes:
                    </p>
                    <ol className="list-decimal pl-5 text-[12px] text-[#A7B4C6] mt-2 space-y-1">
                        <li>
                            On <span className="text-[#2EE6D6]">Recover</span>, the bot fetches the
                            current open-order set from AsterDex. Any <Codey>gd-</Codey> order that is
                            no longer open (because it filled while we were down) is marked{" "}
                            <Codey>FILLED</Codey> in MongoDB and the corresponding follow-up SELL (or
                            self-heal BUY) is queued for placement on the next tick.
                        </li>
                        <li>
                            The user-data WebSocket then catches up on the live tail of{" "}
                            <Codey>executionReport</Codey> events the moment it reconnects, so any fill
                            that happens after Recover is processed in real time.
                        </li>
                    </ol>
                </Card>

                <SubTitle>WebSocket disconnects</SubTitle>
                <Card accent="#FFB02044">
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        Both market and user-data WebSockets use exponential backoff (1 s → 30 s cap)
                        with jitter, plus a 60 s keepalive ping for the listenKey. While disconnected,
                        the engine continues to use the last-known price and the next tick after
                        reconnect re-validates the ladder. If the WS stays down past the freshness
                        budget, the dashboard&apos;s tick-age indicator turns red and placement of NEW
                        orders is throttled.
                    </p>
                </Card>

                <SubTitle>Partial fills</SubTitle>
                <Card accent="#3DA7FF44">
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        AsterDex may fill a 0.0313 BTC order in several smaller chunks. Each chunk is
                        captured as a <Codey>PARTIALLY_FILLED</Codey> execution report. The engine
                        accumulates filled qty + value to compute a real average fill price, and
                        realized PnL is booked from the per-slice deltas — without double counting when
                        the full fill arrives. The Grid Drill-Down dialog shows the fill % progress
                        bar live.
                    </p>
                </Card>

                <SubTitle>Foreign / manual orders on the wallet</SubTitle>
                <Card>
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        If you place a manual trade outside the bot, its clientOrderId will not start
                        with <Codey>gd-</Codey>. The bot increments the{" "}
                        <Codey>foreign_orders_ignored</Codey> counter (visible in Operations &amp;
                        Security) and otherwise leaves your trade alone.
                    </p>
                </Card>

                <SubTitle>Ghost / stale dashboard orders</SubTitle>
                <Card>
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        All orders carry a <Codey>(mode, use_testnet)</Codey> tag at write-time. The
                        Open Orders endpoint filters by the current context, so paper records cannot
                        leak into a LIVE view and testnet vs mainnet never mix. Switching out of paper
                        also pre-emptively marks remaining paper records as <Codey>STALE</Codey>.
                    </p>
                </Card>

                <SubTitle>AsterDex API rejects an order</SubTitle>
                <Card>
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        Each rejection (insufficient funds, min notional, price filter, etc.) is
                        captured in the event log with the AsterDex error code. The bot tries the
                        configured <Codey>fallback_tif</Codey> (GTC if the post-only GTX is refused
                        because it would cross the spread). Persistent rejection patterns surface as a
                        red banner; manual intervention is then expected.
                    </p>
                </Card>

                <SubTitle>Network / region change</SubTitle>
                <Card>
                    <p className="text-[12px] text-[#A7B4C6] leading-relaxed">
                        AsterDex blocks US egress IPs. This VPS is in India (whitelisted). If you move
                        deployment, you must either host in a whitelisted region or re-introduce a
                        SOCKS5/WireGuard tunnel — the code path for proxy use was removed but can be
                        re-added.
                    </p>
                </Card>

                {/* === 6. API === */}
                <SectionTitle icon={Server} anchor="api">Backend API Reference</SectionTitle>
                <Card>
                    <div className="text-[12px] font-mono text-[#A7B4C6] leading-relaxed space-y-1">
                        <div><span className="text-[#2BE38B]">GET</span>  <Codey>/api/bot/status</Codey> — full runtime snapshot, live balances</div>
                        <div><span className="text-[#2BE38B]">GET</span>  <Codey>/api/bot/config</Codey> — current trading config</div>
                        <div><span className="text-[#2BE38B]">GET</span>  <Codey>/api/bot/grid</Codey> — ladder snapshot (active buys/sells + fill telemetry)</div>
                        <div><span className="text-[#2BE38B]">GET</span>  <Codey>{`/api/bot/grid/{grid_id}`}</Codey> — per-grid timeline, trades, partial-fill state</div>
                        <div><span className="text-[#2BE38B]">GET</span>  <Codey>/api/bot/orders?status=open</Codey> — current-context open orders only</div>
                        <div><span className="text-[#2BE38B]">GET</span>  <Codey>/api/bot/trades</Codey>, <Codey>/api/bot/pnl</Codey>, <Codey>/api/bot/pnl/daily</Codey> — all served from the persisted trade ledger (restart-proof, matches /status) and scoped to the current symbol/mode/network</div>
                        <div><span className="text-[#2BE38B]">GET</span>  <Codey>/api/bot/price-history</Codey> — last ~720 mid-price ticks</div>
                        <div><span className="text-[#3DA7FF]">POST</span> <Codey>/api/bot/start</Codey> <span className="text-[#6F7D92]">{`{}`}</span>, <Codey>/api/bot/stop</Codey></div>
                        <div><span className="text-[#3DA7FF]">POST</span> <Codey>/api/bot/mode</Codey> <span className="text-[#6F7D92]">{`{mode: "paper"|"live"}`}</span></div>
                        <div><span className="text-[#3DA7FF]">POST</span> <Codey>/api/bot/network</Codey> <span className="text-[#6F7D92]">{`{use_testnet: bool}`}</span></div>
                        <div><span className="text-[#3DA7FF]">POST</span> <Codey>/api/bot/recover</Codey> — reconciles MongoDB with AsterDex (LIVE) or resets ladder (PAPER)</div>
                        <div><span className="text-[#3DA7FF]">POST</span> <Codey>/api/bot/exclusions</Codey> — replace the set of BUY exclusion ranges</div>
                        <div><span className="text-[#3DA7FF]">POST</span> <Codey>/api/bot/kill-switch</Codey> <span className="text-[#6F7D92]">{`{engaged: bool}`}</span></div>
                        <div><span className="text-[#3DA7FF]">POST</span> <Codey>/api/bot/config</Codey> — patch trading parameters</div>
                        <div><span className="text-[#2BE38B]">GET</span>  <Codey>/api/bot/events</Codey> + SSE stream <Codey>/api/bot/events/stream</Codey></div>
                    </div>
                </Card>

                {/* Footer */}
                <div className="mt-12 mb-4">
                    <Card accent="rgba(46,230,214,0.20)" bg="rgba(46,230,214,0.04)">
                        <div className="flex items-center gap-3">
                            <Database size={16} color="#2EE6D6" />
                            <p className="text-[12px] text-[#A7B4C6]">
                                <span className="text-[#E6EDF6] font-semibold">Source of truth:</span>{" "}
                                AsterDex Spot V3 exchange book. MongoDB is an authoritative ledger of every
                                event the bot witnessed. Anything inconsistent between the two is resolved
                                by calling <Codey>Recover</Codey>, which always treats AsterDex as winning.
                            </p>
                        </div>
                    </Card>
                </div>

                <div className="text-center text-[10px] text-[#6F7D92] mt-8 mb-4 tracking-[0.16em] uppercase">
                    AsterDex GridDesk · Built for resilient, transparent on-chain market making
                </div>
            </div>
        </div>
    );
}
